import styled, { css } from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { desktop1100, desktopMidWide, tablet } from '@shared/ui/atoms/MediaQueries';

/**
 * Cards sit on a white page, so their border does the separating rather than a background tint.
 * The only tinted surfaces are the table header, the segmented tray and the stat cards.
 */
export const LockerPageBody = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 16px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (${tablet}) {
    padding: 32px 16px 40px;
  }
`

export const LockerCard = styled.div<{ accent?: string; }>`
  background-color: white;
  border: 1px solid ${props => props.accent ?? Colours.lightGrey};
  border-radius: 12px;
`

export const LockerCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${Colours.iconGrey};
`

export const LockerCardFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 20px;
`

export const LockerTitleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (${tablet}) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
  }
`

export const LockerTitleActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
`

// react-select sizes to its container, so give it a width inside the flex row
export const OfficeSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (${tablet}) {
    flex: 0 0 260px;
    min-width: 260px;
  }
`

/* Stat strip --------------------------------------------------------------------------------- */

export const StatStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (${tablet}) {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
`

export const StatCard = styled.button<{ selected?: boolean; hideNarrow?: boolean; }>`
  display: ${props => props.hideNarrow ? 'none' : 'flex'};
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  text-align: left;
  width: 100%;
  cursor: pointer;
  background-color: white;
  border: 1px solid ${props => props.selected ? Colours.blue : Colours.lightGrey};
  border-radius: 10px;
  padding: 12px 14px;
  transition: border-color 0.15s ease-in-out;

  &:hover {
    border-color: ${props => props.selected ? Colours.blue : Colours.mildGrey};
  }

  @media (${tablet}) {
    display: flex;
    border-radius: 12px;
    padding: 16px 20px;
  }
`

export const StatValue = styled.span<{ colour?: string; }>`
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 28px;
  color: ${props => props.colour ?? Colours.black};

  @media (${tablet}) {
    font-size: 30px;
    line-height: 34px;
  }
`

/* Page body: main column plus rail ------------------------------------------------------------ */

/**
 * The rail only moves alongside the table once there is room for both. Below that it sits under the
 * table full width, which is better than squeezing a five-column table into what is left of 1100px.
 */
export const LockerBodyLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (${desktopMidWide}) {
    flex-direction: row;
    align-items: flex-start;
  }
`

export const LockerMainColumn = styled.div`
  flex: 1;
  min-width: 0;
`

export const LockerRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (${desktopMidWide}) {
    width: 340px;
    flex-shrink: 0;
  }
`

/* Toolbar ------------------------------------------------------------------------------------- */

export const TableToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid ${Colours.lightGrey};

  @media (${tablet}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
  }
`

export const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 12px;
  border: 1px solid hsl(0, 0%, 80%);
  border-radius: 8px;

  @media (${tablet}) {
    height: 38px;
    border-radius: 6px;
    min-width: 300px;
  }
`

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-family: proxima-nova, sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: ${Colours.black};

  &::placeholder {
    color: ${Colours.mildGrey};
  }
`

export const SegmentedControl = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background-color: ${Colours.veryLightGrey};
  border-radius: 8px;
`

export const Segment = styled.button<{ selected?: boolean; }>`
  flex: 1;
  cursor: pointer;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-family: "soleil", sans-serif;
  font-size: 14px;
  line-height: 14px;
  font-weight: ${props => props.selected ? 600 : 400};
  color: ${props => props.selected ? 'white' : Colours.black};
  background-color: ${props => props.selected ? Colours.blue : 'transparent'};
  transition: background-color 0.15s ease-in-out;
  white-space: nowrap;

  @media (${tablet}) {
    flex: initial;
    padding: 6px 14px;
  }
`

/* Tables -------------------------------------------------------------------------------------- */

const tableGrid = css<{ columns: string; }>`
  display: grid;
  align-items: center;
  gap: 16px;

  /* Grid items default to min-width auto, which lets long names push a column past its track. */
  > * {
    min-width: 0;
  }

  @media (${desktop1100}) {
    grid-template-columns: ${props => props.columns};
  }
`

export const TableHeaderRow = styled.div<{ columns: string; }>`
  ${tableGrid};
  display: none;
  padding: 10px 20px;
  background-color: ${Colours.veryLightGrey};

  @media (${desktop1100}) {
    display: grid;
  }
`

export const TableRow = styled.div<{ columns: string; }>`
  ${tableGrid};
  grid-template-columns: 1fr auto;
  min-height: 64px;
  padding: 12px 14px;
  border-bottom: 1px solid ${Colours.iconGrey};
  transition: background-color 0.15s ease-in-out;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${Colours.blue02};
  }

  @media (${desktop1100}) {
    min-height: 0;
    padding: 12px 20px;
  }
`

/** Columns that only earn their space once the row is wide enough to be a real table. */
export const WideCell = styled.div`
  display: none;
  min-width: 0;

  @media (${desktop1100}) {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  /* A long name truncates inside its column rather than running into the next one. */
  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

/** Same, for a cell that stacks its contents rather than sitting them in a row. */
export const WideBlockCell = styled.div`
  display: none;
  min-width: 0;

  @media (${desktop1100}) {
    display: block;
  }
`

/** The line of detail that replaces those columns on a phone. */
export const NarrowSubtitle = styled.div`
  @media (${desktop1100}) {
    display: none;
  }
`

export const TableCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const TableRowActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
`

/* Small pieces -------------------------------------------------------------------------------- */

export const StatusPill = styled.div<{ backgroundColour: string; }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: ${props => props.backgroundColour};
`

export const StatusDot = styled.span<{ colour: string; }>`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  flex-shrink: 0;
  background-color: ${props => props.colour};
`

export const CountPill = styled.div<{ backgroundColour: string; }>`
  display: flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background-color: ${props => props.backgroundColour};
`

export const InitialsAvatar = styled.div<{ size?: number; }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${props => props.size ?? 28}px;
  height: ${props => props.size ?? 28}px;
  border-radius: 999px;
  background-color: ${Colours.blue10};
  font-family: "soleil", sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 13px;
  color: ${Colours.blue};
`

export const SquareIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  cursor: pointer;
  background-color: white;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 6px;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: ${Colours.veryLightGrey};
  }

  i {
    display: inline-flex;
  }
`

export const IconTile = styled.div<{ tileSize: number; backgroundColour: string; }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${props => props.tileSize}px;
  height: ${props => props.tileSize}px;
  border-radius: ${props => props.tileSize >= 72 ? 14 : (props.tileSize >= 52 ? 12 : 10)}px;
  background-color: ${props => props.backgroundColour};
`

export const ProgressTrack = styled.div<{ height?: number; }>`
  width: 100%;
  height: ${props => props.height ?? 6}px;
  border-radius: 999px;
  background-color: ${Colours.iconGrey};
  overflow: hidden;
`

export const ProgressFill = styled.div<{ percent: number; }>`
  width: ${props => Math.max(0, Math.min(100, props.percent))}%;
  height: 100%;
  border-radius: 999px;
  background-color: ${Colours.blue};
`

/* Hero cards (member views) -------------------------------------------------------------------- */

export const HeroRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (${desktop1100}) {
    flex-direction: row;
    align-items: stretch;
  }
`

export const HeroCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;

  @media (${tablet}) {
    flex-direction: row;
    align-items: center;
    gap: 24px;
    padding: 24px;
  }
`

export const HeroCardDetails = styled.div`
  flex: 1;
  min-width: 0;
`

export const HeroValue = styled.span<{ colour?: string; }>`
  display: block;
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: 28px;
  line-height: 32px;
  letter-spacing: -0.01em;
  color: ${props => props.colour ?? Colours.black};

  @media (${tablet}) {
    font-size: 34px;
    line-height: 36px;
  }
`

export const HeroTileValue = styled.span<{ colour?: string; }>`
  font-family: "soleil", sans-serif;
  font-weight: 700;
  font-size: 26px;
  line-height: 26px;
  color: ${props => props.colour ?? Colours.blue};

  @media (${tablet}) {
    font-size: 30px;
    line-height: 30px;
  }
`

/**
 * The button atoms fill their container up to a fixed max-width, which is right for a form and
 * wrong inside a table row or beside a hero. These wrappers size them without forking the atom,
 * and hold every touch target at 44px until there is a mouse-sized screen to shrink into.
 */
export const HeroAction = styled.div`
  width: 100%;
  flex-shrink: 0;

  button {
    width: 100%;
    max-width: none;
    height: 44px;
    min-height: 44px;
  }

  @media (${tablet}) {
    width: auto;
    min-width: 190px;

    button {
      height: 40px;
      min-height: 40px;
    }
  }
`

export const RowAction = styled.div<{ width?: number; }>`
  flex-shrink: 0;

  button {
    width: 100%;
    max-width: none;
    height: 44px;
    min-height: 44px;
    white-space: nowrap;
  }

  @media (${desktop1100}) {
    width: ${props => props.width ?? 96}px;

    button {
      height: 32px;
      min-height: 32px;
    }
  }
`

export const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
`
